/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./TeamModal.css";
import { teamMembers } from "../data/teamData";

const TeamModal = ({ open, onClose }) => {
  const [isEnlarged, setIsEnlarged] = useState(false);

  const handleImageClick = () => {
    setIsEnlarged(true);
  };

  const handleCloseEnlarged = () => {
    setIsEnlarged(false);
  };

  // Disable scrollbar and prevent scrolling when modal is open or image is enlarged
  useEffect(() => {
    if (open || isEnlarged) {
      // Store original scroll position
      const scrollY = window.scrollY;

      // Add class to body to control other elements' z-index
      document.body.classList.add("modal-open");

      // Disable scrolling on both html and body
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      // Remove class from body
      document.body.classList.remove("modal-open");

      // Restore scrolling and position
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      if (!open && !isEnlarged) {
        document.body.classList.remove("modal-open");
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
      }
    };
  }, [open, isEnlarged]);

  if (!open) return null;

  return (
    <div
      className="team-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
        }
      }}
      tabIndex="-1"
    >
      <div className="team-modal" role="document" onClick={(e) => e.stopPropagation()}>
        <button className="team-modal-close" onClick={onClose}>
          ×
        </button>

        <div className="team-modal-content">
          <h1 className="team-modal-title">
            <span>MEET THE CREATOR</span>
          </h1>

          <div className="team-modal-card" style={{ position: "relative", overflow: "hidden" }}>
            {/* Simple animated background gradient - no heavy particles */}
            <div className="simple-particle-bg"></div>

            {/* Team card content is above the background */}
            <div className="team-card-content" style={{ position: "relative", zIndex: 101 }}>
              <div className="carousel-container">
                <div className="carousel-slide">
                  <div className="team-member">
                    <div className="team-member-header">
                      <button
                        className="team-member-image"
                        onClick={handleImageClick}
                        style={{
                          border: "none",
                          background: "transparent",
                          padding: 0,
                          cursor: "pointer",
                        }}
                      >
                        <img src={teamMembers[0].image} alt="Team Member" />
                      </button>
                      <div className="team-member-intro">
                        <p className="team-description team-modal-gold-text">
                          **Guiding the Voyage Through Time & Dreams** Introducing the visionary behind the fleet.
                        </p>
                        <div className="expertise-tags">
                          <span className="expertise-tag">{teamMembers[0].role}</span>
                        </div>
                        <h3 className="team-modal-name-style team-modal-gold-text">
                          {teamMembers[0].name.split(" ").map((namePart, i) => (
                            <React.Fragment key={`name-${namePart}-${i}`}>
                              {namePart}
                              <br />
                            </React.Fragment>
                          ))}
                        </h3>
                        <p className="job-title team-modal-gold-text">{teamMembers[0].jobTitle}</p>
                      </div>
                    </div>
                    <div className="team-member-details">
                      <div className="details-grid">
                        <div className="detail-section">
                          <div className="member-description">
                            <p className="team-modal-gold-text">{teamMembers[0].description}</p>
                          </div>
                          <div className="member-mission">
                            <h4 className="team-modal-gold-text">Mission</h4>
                            <p className="team-modal-gold-text">{teamMembers[0].mission}</p>
                          </div>
                        </div>
                        <div className="detail-section">
                          <div className="member-expertise">
                            <h4 className="team-modal-gold-text">Expertise</h4>
                            <div className="expertise-tags">
                              {teamMembers[0].expertise.map((skill, index) => (
                                <span key={`skill-${skill}-${index}`} className="expertise-tag">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="member-achievements">
                            <h4 className="team-modal-gold-text">Key Achievements</h4>
                            <ul>
                              {teamMembers[0].achievements.map((achievement, index) => (
                                <li
                                  key={`achievement-${achievement.slice(0, 10)}-${index}`}
                                  className="achievement-item team-modal-gold-text"
                                >
                                  {achievement}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                      <div className="member-quote">
                        <blockquote className="team-modal-gold-text">"{teamMembers[0].quote}"</blockquote>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enlarged Image Modal */}
        {isEnlarged && (
          <div className="avatar-modal">
            <button
              className="modal-backdrop-button"
              onClick={handleCloseEnlarged}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  handleCloseEnlarged();
                }
              }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "transparent",
                border: "none",
                cursor: "default",
                zIndex: -1,
              }}
              aria-label="Close avatar modal"
              tabIndex={-1}
            />
            <div className="avatar-modal-content">
              <div className="avatar-container">
                <img src={teamMembers[0].image} alt="Enlarged Team Member" />
                <button
                  className="close-modal-btn"
                  onClick={handleCloseEnlarged}
                  title="Close"
                  aria-label="Close avatar modal"
                >
                  ✕
                </button>
              </div>
              <div className="avatar-enlarged-details">
                <h3 className="team-modal-gold-text">{teamMembers[0].name}</h3>
                <p className="enlarged-role team-modal-gold-text">{teamMembers[0].role}</p>
                <p className="enlarged-job-title team-modal-gold-text">{teamMembers[0].jobTitle}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TeamModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TeamModal;
